import React, { useContext, useState, useEffect, useRef } from 'react';
// Adiciona o import no topo
import UpgradeModal from '../ModalUpgrade';

import {
    Package, PlusCircle, Pencil, Trash2, Search,
    Loader2, X, ImagePlus, Check, FileDown
} from 'lucide-react';

import styles from './styles.module.css';
import { Context } from '../../context/UserContext';
import { generateCatalogPdf } from '../../utils/generateCatalogPdf';

const EMPTY_FORM = { name: '', description: '', price: '', stock: '', category: '', image: null };

export default function ManageProducts() {
    const {
        createProduct, getMyProducts, updateProduct, deleteProduct, searchProducts,
        products, setProducts, user
    } = useContext(Context);

    const [view, setView]               = useState('list');
    const [editing, setEditing]         = useState(null);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [preview, setPreview]         = useState(null);
    const [loading, setLoading]         = useState(false);
    const [fetching, setFetching]       = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [planLimit, setPlanLimit]     = useState(5);
    const [planName, setPlanName]       = useState('free');
    const [generatingPdf, setGeneratingPdf] = useState(false);

    const fileRef = useRef();
    const token   = JSON.parse(localStorage.getItem('token'));

    /* ── Carrega produtos ao montar ── */
    useEffect(() => {
        (async () => {
            setFetching(true);
            const data = await getMyProducts(token);
            // data é um array com .limit e .plan como propriedades extras
            setProducts(Array.from(data)); // copia limpa para o state (sem as props extras)
            setPlanLimit(data.limit ?? 5);
            setPlanName(data.plan  ?? 'free');
            setFetching(false);
        })();
        // eslint-disable-next-line
    }, []);

    /* ── Libera URL de preview ── */
    useEffect(() => {
        return () => {
            if (preview && !preview.startsWith('http')) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    /* ── Derivados ── */
    const atLimit      = products.length >= planLimit;
    const usagePercent = Math.min((products.length / planLimit) * 100, 100);
    const canDownloadCatalog = planName === 'plus' || planName === 'premium';
   // Adiciona o estado
    const [openUpgradeModal, setOpenUpgradeModal] = useState(false);

    // Troca o openCreate
    const openCreate = () => {
        if (atLimit) {
            setOpenUpgradeModal(true); // 🔥 abre modal em vez de bloquear
            return;
        }
        setEditing(null);
        setForm(EMPTY_FORM);
        setPreview(null);
        setView('form');
    };

    const openEdit = (product) => {
        setEditing(product);
        setForm({
            name:        product.name,
            description: product.description || '',
            price:       product.price,
            stock:       product.stock,
            category:    product.category || '',
            image:       null,
        });
        setPreview(product.image || null);
        setView('form');
    };

    const cancelForm = () => {
        setView('list');
        setEditing(null);
        setForm(EMPTY_FORM);
        setPreview(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setForm(prev => ({ ...prev, image: file }));
        if (preview && !preview.startsWith('http')) URL.revokeObjectURL(preview);
        setPreview(URL.createObjectURL(file));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    /* ── Submit criar / editar ── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append('name',        form.name);
        formData.append('description', form.description);
        formData.append('price',       form.price);
        formData.append('stock',       form.stock);
        formData.append('category',    form.category);
        if (form.image) formData.append('image', form.image);

        let result;
        if (editing) {
            result = await updateProduct(token, editing._id, formData);
            if (!result.error) {
                setProducts(prev => prev.map(p => p._id === editing._id ? result.product : p));
            }
        } else {
            result = await createProduct(token, formData);
            if (!result.error) {
                setProducts(prev => [result.product, ...prev]);
            }
        }

        setLoading(false);
        if (!result.error) cancelForm();
    };

    /* ── Delete ── */
    const handleDelete = async (id) => {
        if (!window.confirm('Remover este produto?')) return;
        const result = await deleteProduct(token, id);
        if (!result?.error) setProducts(prev => prev.filter(p => p._id !== id));
    };

    /* ── Busca ── */
    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) { setSearchResults(null); return; }
        setFetching(true);
        const results = await searchProducts(token, searchQuery);
        setSearchResults(results);
        setFetching(false);
    };

    const clearSearch = () => { setSearchQuery(''); setSearchResults(null); };

    /* ── Baixar catálogo em PDF ── */
    const handleDownloadCatalog = async () => {
        if (!canDownloadCatalog || products.length === 0) return;
        setGeneratingPdf(true);
        try {
            await generateCatalogPdf(products, user);
        } catch (err) {
            console.error('Erro ao gerar catalogo:', err);
        } finally {
            setGeneratingPdf(false);
        }
    };

    const displayList = searchResults !== null ? searchResults : products;

    /* ── Render ── */
    return (
        <div className={styles.container}>
             {/* 🔥 Modal de upgrade */}
            {openUpgradeModal && (
                <UpgradeModal
                    currentPlan={planName}
                    setOpenUpgradeModal={setOpenUpgradeModal}
                />
            )}

            {/* ── Barra de uso ── */}
            <div className={styles.usageBar}>
                <div className={styles.usageHeader}>
                    <span className={styles.usageLabel}>
                        {products.length} / {planLimit} produtos
                    </span>
                    <span className={`${styles.planBadge} ${styles[`plan_${planName}`]}`}>
                        {planName.charAt(0).toUpperCase() + planName.slice(1)}
                    </span>
                </div>
                <div className={styles.usageTrack}>
                    <div
                        className={`${styles.usageFill} ${atLimit ? styles.usageFull : ''}`}
                        style={{ width: `${usagePercent}%` }}
                    />
                </div>
                {atLimit && (
                    <p className={styles.usageLimit}>
                        Limite atingido. Faça upgrade para adicionar mais produtos.
                    </p>
                )}
            </div>

            {/* ════════════ LISTAGEM ════════════ */}
            {view === 'list' && (
                <>
                    <div className={styles.topBar}>
                        <form className={styles.searchForm} onSubmit={handleSearch}>
                            <input
                                type="text"
                                placeholder="Buscar produto..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className={styles.searchInput}
                            />
                            {searchQuery && (
                                <button type="button" className={styles.clearSearch} onClick={clearSearch}>
                                    <X size={16} />
                                </button>
                            )}
                            <button type="submit" className={styles.searchBtn}>
                                <Search size={18} />
                            </button>
                        </form>
                        
                        {/* Botão de catálogo — só plus/premium */}
                        {canDownloadCatalog && (
                            <button
                                className={styles.catalogBtn}
                                onClick={handleDownloadCatalog}
                                disabled={generatingPdf || products.length === 0}
                                title={products.length === 0 ? 'Nenhum produto no estoque' : 'Baixar catálogo em PDF'}
                            >
                                {generatingPdf
                                    ? <Loader2 size={16} className={styles.spinner} />
                                    : <FileDown size={16} />
                                }
                                {generatingPdf ? 'Gerando...' : 'Catálogo PDF'}
                            </button>
                        )}

                        <button
                            className={styles.newBtn}
                            onClick={openCreate}
                            title={atLimit ? `Limite de ${planLimit} produtos atingido` : 'Novo produto'}
                        >
                            <PlusCircle size={18} />
                            Novo Produto
                        </button>
                    </div>

                    {fetching ? (
                        <div className={styles.loadingWrap}><Loader2 className={styles.spinner} /></div>
                    ) : displayList.length === 0 ? (
                        <p className={styles.empty}>Nenhum produto encontrado.</p>
                    ) : (
                        <div className={styles.productGrid}>
                            {displayList.map(product => (
                                <div key={product._id} className={styles.productCard}>
                                    <div className={styles.productImageWrap}>
                                        {product.image ? (
                                            <img src={product.image} alt={product.name} className={styles.productImage} />
                                        ) : (
                                            <div className={styles.productImagePlaceholder}>
                                                <Package size={32} />
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.productInfo}>
                                        <span className={styles.productName}>{product.name}</span>
                                        {product.category && (
                                            <span className={styles.productCategory}>{product.category}</span>
                                        )}
                                        <span className={styles.productPrice}>
                                            R$ {Number(product.price).toFixed(2)}
                                        </span>
                                        <span className={styles.productStock}>Estoque: {product.stock}</span>
                                    </div>
                                    <div className={styles.productActions}>
                                        <button className={styles.editBtn} onClick={() => openEdit(product)} title="Editar">
                                            <Pencil size={16} />
                                        </button>
                                        <button className={styles.deleteBtn} onClick={() => handleDelete(product._id)} title="Remover">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ════════════ FORMULÁRIO ════════════ */}
            {view === 'form' && (
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formHeader}>
                        <h3 className={styles.formTitle}>
                            {editing ? 'Editar Produto' : 'Novo Produto'}
                        </h3>
                        <button type="button" className={styles.cancelBtn} onClick={cancelForm}>
                            <X size={18} /> Cancelar
                        </button>
                    </div>

                    <div className={styles.imageUploadWrap} onClick={() => fileRef.current.click()}>
                        {preview ? (
                            <img src={preview} alt="preview" className={styles.imagePreview} />
                        ) : (
                            <div className={styles.imagePlaceholder}>
                                <ImagePlus size={28} />
                                <span>Clique para adicionar foto</span>
                            </div>
                        )}
                        <input ref={fileRef} type="file" accept="image/*"
                            style={{ display: 'none' }} onChange={handleImageChange} />
                    </div>

                    <fieldset className={styles.fieldset}>
                        <legend>Informações do Produto</legend>
                        <input type="text"   name="name"        placeholder="Nome *"       value={form.name}        onChange={handleChange} required />
                        <textarea           name="description" placeholder="Descrição"    value={form.description} onChange={handleChange} className={styles.textarea} rows={3} />
                        <input type="text"   name="category"    placeholder="Categoria"    value={form.category}    onChange={handleChange} />
                        <div className={styles.row}>
                            <input type="number" name="price" placeholder="Preço (R$) *" value={form.price} onChange={handleChange} min="0" step="0.01" required />
                            <input type="number" name="stock" placeholder="Estoque"      value={form.stock} onChange={handleChange} min="0" />
                        </div>
                    </fieldset>

                    <button type="submit" className={styles.submitBtn} disabled={loading}>
                        {loading
                            ? <Loader2 size={18} className={styles.spinner} />
                            : <><Check size={18} /> {editing ? 'Salvar Alterações' : 'Criar Produto'}</>
                        }
                    </button>
                </form>
            )}
        </div>
    );
}